import { useState, type ReactNode } from 'react';
import { CountedLabel } from './CountedLabel';
import { Wrench } from 'lucide-react';
import { useCategories } from './catalogCategories';
import { namedIn, shownId } from './partsCatalogue';
import { PART_GROUPS, groupOfCategory, type PartGroup, type PartItem, type Side }
  from './partsRun';
import { sideWord } from './subStepText';
import { subStepOutputName, usedCount, type SubStep, type UsedPart }
  from './stepsRun';
import { MADE_ID_BASE, makesHardware } from './subStepKinds';
import { CallModal } from './CallBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT ONE SUB-STEP NEEDS IN HAND, as chips: its components, its hardware and its tools.
//
// Drawn one group per call (`only`): the sub-step table lays the three out as columns of its
// own. It was also a gesture's own page, the three groups stacked under its sentence — gone
// with the pages, the table saying the same of every gesture at once.

// A chip, the shape the phases already use for a part under a sentence.
//
// AMBER, AND IT USED TO BE GREEN. Green now says COMBINING — it is the family colour of the
// kind label two columns to the left (see subStepKindTone), and in Phase 5 that label sits
// directly above this very row. One colour cannot be both "a supplied part" and "this gesture
// fastens something": a reader who has learnt either meaning reads the other one wrong every
// time. Amber is the hue furthest from both family colours AND from the violet below, and it
// is warm where they are cool, which is the distinction it is carrying: warm is a thing you
// hold, cool is a reading the app made of the sentence.
//
// OUTLINED, NOT FILLED, and that is the second channel. Colour alone would put the whole
// vocabulary on one axis; fill splits it in two — outlined is an OBJECT (this part, that
// piece), filled is the app's LABEL ABOUT the gesture. The distinction then survives a reader
// who cannot separate the hues, and it survives us changing our minds about which hues.
//
// NOT PRESSABLE, here. Phase 5 draws the same part as a toggle (see the ticked row there, which
// borrows `partTone` so the two read as one list at two moments); this phase is showing what
// the analysis SAID a gesture needs, and disagreeing with it is done by re-running it or by
// editing the sub-step, not by unticking a word. So no hover, no cursor and no border that
// lifts: a chip that looks pressable and is not is a worse lie than a grey one.
//
// OVER TWO LINES WHERE THE NAME NEEDS IT, capped at 14rem. Kept to one line, a made piece's
// name ("Seated chair assembly with two screws tightened and third chipboard screw inserted")
// made its column the width of the sentence and the table several screens across.
//
// A ROUNDED BOX AND NOT A PILL: rounded-full on a box two lines tall puts the second line
// outside the curve, and the chip reads as cut in half. At one line, rounded-xl on a chip this
// height is the same pill it always was.
//
// `w-max` UNDER THE CAP, so a chip is as wide as its name up to 14rem and wraps only past it.
// Without a set width a table is free to squeeze a wrapping chip down to its longest word — a
// column of one-word lines.
const nowrap = 'inline-block w-max max-w-[14rem] whitespace-normal text-center leading-4';
// Exported for Phase 5's ticked chips: one part, one colour, wherever it is drawn.
//
// FILLED, not outlined. An outlined chip is legible on white and a thin ring of colour on
// the blueprint page — and these name the objects a gesture takes and leaves, which is the
// thing the eye is looking for on that card. Flat and at amber-600, the same pair the tree's
// nodes use (see PieceNode): one part, one colour, in a list or in a diagram. No gradient —
// a shaded chip reads as a button, and none of these can be pressed.
export const partTone = 'border-amber-700 bg-amber-600 text-white';
// Exported for the AVAILABLE PARTS column, which draws the same two objects — a supplied
// part and a made piece — outside a sub-step's own list.
export const chip = `${nowrap} rounded-xl border px-3 py-1 text-xs font-semibold ${partTone}`;

// AND VIOLET FOR A PIECE THE MANUAL NEVER SUPPLIED. Amber is a part off the parts list, with
// an id, a letter and a supplied count; this is what two or three of those BECAME under an
// earlier sub-step's hands — it has a name and nothing else, since nobody sells it and no
// diagram labels it. Drawn the same shape in both places it appears (the OUTPUT column where
// it is made, and the parts of a later gesture that picks it up) so the two read as one
// object at two moments, which is the same argument the amber chip is drawn on — and outlined
// like it, because it is an object too, however it came to exist.
//
// Exported because the table draws it in a cell of its own, with no parts around it.
export const outputChip =
  `${nowrap} rounded-xl border px-3 py-1 text-xs font-semibold border-violet-700 bg-violet-600 text-white`;

// The name a part is called by: its name in the language being read, then the letter the
// diagram labels it with — see namedIn, which is where that choice lives for every control
// that makes it. A hook because the language is one, and because both callers of the chips
// also name parts outside them (a hole's piece, on the sub-step page).
// THE ID LEADS THE NAME, "4 - JCBC screw (E)". Every answer in this phase is written in ids —
// the prompt's lists, the sub-steps' "parts", the reconciliation's columns — and the chips
// were the one place that showed only the prose, so checking a sub-step against the block it
// was given meant translating names back into numbers by eye. The letter stays where it was:
// it is the manual's own callout, and the id is the app's.
// AND WHICH OF A MIRROR PAIR, where the gesture said — "03 - Side rail (C, Left)". The side is
// NOT a property of the part, so it cannot be looked up here: the part is both hands, and only
// the entry that uses it says which one. Passed by the caller that has that entry; every
// caller that names a part outside a gesture (a hole's piece, a chip in the AVAILABLE column)
// passes nothing and gets the bracket it always got. See sideWord, which is the one place the
// bracket is spelt.
export function usePartLabel(items: PartItem[]) {
  const { language } = useLanguage();
  return (id: string, side?: Side | null) => {
    const p = items[Number(id)];
    if (!p) return `#${id}`;
    // ONE HIGHER THAN THE INDEX, which is what every prompt shows and every answer is written
    // in — see shownId. The chips are read beside those prompts.
    const named = namedIn(p, language) || `#${id}`;
    // INSIDE THE CALLOUT'S OWN BRACKET rather than after it, which is what withLetter would
    // give: "(C, Left)" names one of two rails, "(C) Left" reads as a rail put leftwards.
    // Only where there is both a letter and a side — a part the manual never lettered has no
    // bracket to put the side in, and gets one of its own.
    const mark = `${p.letter ?? ''}${sideWord(side, language)}`;
    return `${shownId(id)} - ${mark ? `${named} (${mark})` : named}`;
  };
}

// One thing a gesture has in hand: a part it lists, a tool named only in "tools" — which
// carries no quantity and no consumption, because a tool is used and handed back — or an
// earlier sub-step's OUTPUT, which is neither: it is the piece several of the parts became.
type Held = { key: string; id: string; part: UsedPart | null; output?: MadePiece };

// A PIECE AN EARLIER SUB-STEP LEFT, with an id of its own. It had none for a long time and
// was named by the ids it was made FROM — a workaround with a real cost: the thing on screen
// had no address, so nothing could refer to it in one word, and the prompt had to spend a
// paragraph explaining how to point at a piece that does not exist in any list.
//
// ITS ID IS ITS MAKER. A piece is whatever the sub-step that made it left, so the sub-step's
// own address names it exactly and cannot collide with a part's — those are numbers.
// `group` is which of the three columns it belongs in, read off the maker's kind rather than
// off the parts it swallowed: preparing hardware leaves an assembled HARDWARE part and every
// other kind an assembled component part, which is what the kinds themselves say they make.
export type MadePiece = {
  // WHAT IT IS CALLED, on screen and in every map here — "401". One numbering, in gesture
  // order, handed out by pieceIdByKey and promised to the model in the prompt, so the id a
  // later sub-step writes is the id this piece answers to. There was a second field beside
  // this one, `key`, from when the app invented pieces of its own for the placings and their
  // numbers could collide with these; there is one kind of piece now and one name for it.
  id: string;
  name: string;
  group: PartGroup;
  // WHAT IT IS MADE OF, one level down: the parts the gesture that left it took in, as it
  // named them. A piece among them is a piece — "side rail with barrel nut" is made of a
  // rail and a nut, and "frame with side rail" is made of a frame and THAT piece — so this
  // stops at the first level and the chip inside the panel is itself openable.
  //
  // Kept on the piece rather than looked up on demand: the caller that could look it up is
  // the one that already walked the whole sequence to build these, and anybody drawing a
  // chip has the piece and nothing else.
  from: { id: string; qty: number }[];
  // Which gesture left it — "1B" — so the panel can say where to look.
  madeBy: string;
};



// WHAT EACH GESTURE IS HOLDING THAT AN EARLIER ONE MADE, walked in the manual's order: for
// every row, the pieces that existed BEFORE it — a sub-step does not hold its own output while
// it is making it. Keyed twice over, because two vocabularies are in the library: under the
// piece's own id, which is how a sub-step analysed today names it, and under every part id that
// went into it, which is how one analysed before the piece had an id names it.
//
// HERE rather than in the table, because both pages need it and neither can derive it alone: a
// sub-step cannot know what the sub-steps before it left, so it takes the whole sequence, and
// two components building the same map from the same rows is two maps that stop agreeing.
export type RowPieces = {
  // What this gesture is already holding: every piece made before it, under every name a
  // sub-step might call it by.
  held: Map<string, MadePiece>;
  // And what it leaves, which is not in `held` — a gesture does not hold its own output while
  // it is making it, and the OUTPUT column is the one place that needs it.
  //
  // EVERY GESTURE LEAVES ONE, a placing included. There was a second field here, `placed`, for
  // a piece this file invented when a placing came back saying it left nothing: a number, a
  // name joined out of its parts, and the word "(placed)". It drew well and it was in no
  // answer and in no numbering — the model had never heard of it, so the next gesture went on
  // naming the loose parts, and consuming them in the placing would then have counted them
  // twice. The prompt asks a placing for its output now, so there is one kind of piece.
  own?: MadePiece;
};

export function madePieces(
  rows: { key: string; label: string; step?: unknown; sub: SubStep }[],
  language: string,
  // THE IDS, ASSIGNED ELSEWHERE. One sequence runs through the whole manual and three readers
  // print from it — see pieceIdByKey, which is the only place that counts. This function draws
  // what it is given and numbers nothing, so a piece cannot be 104 here and 105 in Phase 5.
  ids: Map<string, { id: string }>,
): Map<string, RowPieces> {
  const out = new Map<string, RowPieces>();
  const running = new Map<string, MadePiece>();
  for (const { key, label, sub } of rows) {
    const held = new Map(running);
    const name = subStepOutputName(sub.output, language);
    const numbered = ids.get(key);
    if (!name || !numbered) {
      out.set(key, { held });
      continue;
    }
    // WHATEVER ITS KIND. A placing leaves a piece exactly as a combining does — the parts it
    // named are inside it — and this branch is now the only one there is. The kinds say what
    // each leaves; see SUB_STEP_KINDS.makes.
    const piece: MadePiece = {
      id: numbered.id,
      name,
      // Only one kind leaves hardware — two pieces of it, joined. Everything else leaves
      // something you could call furniture, a positioned part included. Asked of the kinds
      // themselves (see makesHardware) and not of the integer, which has been renumbered once.
      group: makesHardware(sub.kind) ? 'Hardware' : 'Components',
      // AS THE GESTURE NAMED THEM.
      from: (sub.parts ?? []).map((p) => ({ id: p.id, qty: p.qty ?? 1 })),
      madeBy: label,
    };
    out.set(key, { held, own: piece });
    // UNDER TWO NAMES: its own id, which is what a sub-step analysed today writes, and every part
    // id that went into it, which is what one analysed before the piece had any id writes. NOT
    // the number of the sub-step that made it, which an older analysis wrote as its letter: a
    // sub-step's number is "3", and so is a part's id.
    running.set(piece.id, piece);
    // The piece a gesture is built ONTO is inside the result as much as the piece driven into
    // it: a rail with barrel nuts in it is not a bare rail any more, and a rail held against a
    // frame is not a loose rail either.
    //
    // SUPPLIED PARTS ONLY, and this is the bug that taught the rule. A combining that takes in
    // an earlier PIECE was mapping that piece's id to itself as well — "101" stopped meaning
    // the prepared screw and started meaning the frame the screw went into — so every later
    // gesture that reached for a prepared screw drew the frame instead, and where it already
    // held the frame the two collapsed into one chip and the screw vanished from the row. The
    // availability column, which resolves a piece by its own id and nothing else, went on
    // spending the screws correctly, and the two columns disagreed down the whole step.
    //
    // The legacy mapping is only for answers written before pieces had ids, and those name
    // supplied parts, whose ids are small. Anything from MADE_ID_BASE up already names a piece.
    for (const p of sub.parts ?? []) {
      if (Number(p.id) >= MADE_ID_BASE) continue;
      running.set(p.id, piece);
    }
  }
  return out;
}

// WHAT IS ON THE TABLE AFTER EACH GESTURE, for the step it belongs to and no further. Two
// lists: the supplied parts not yet used up, and the pieces earlier gestures made that nothing
// has picked up again.
//
// WHERE THE STARTING STOCK COMES FROM. Not from Phase 2's repartition — this reader does not
// have it, and reaching for it would tie a table of gestures to a table of totals — but from
// the sub-steps themselves: the step's supply of a part IS what its sub-steps consume of it,
// which is the very rule the prompt states as CRITICAL. So the pool is summed from the answers
// first and spent down through them in order, and a step whose numbers do not add up shows
// that as a part that runs out early rather than as a silent zero.
//
// A NEW POOL PER STEP FOR THE SUPPLIED PARTS, because that half really is about this step: a
// rail left over in step 2 is not stock for step 5. The PIECES are the manual's and run its
// whole length — see the inventory below.
export type Available = {
  made: (MadePiece & { n: number })[];
  // AND WHICH HAND, where the part comes in two — see PartItem.symmetry. A mirror pair is not
  // one part supplied twice: there is intrinsically a left side rail and a right one, they are
  // not interchangeable, and a stock line reading "side rail x2" says the one thing about them
  // that is false. So the pool is counted per hand and the column draws two chips.
  //
  // Absent where the sub-steps did not say which hand they took, which is its own answer and
  // is left visible: an un-sided pair pools as a single x2 chip, and that chip IS the report
  // that Phase 4 has not distinguished them yet.
  // AND WHAT THE STEP WAS GIVEN OF IT, beside what is left. `n` alone says "one rail" and
  // cannot say whether that is the last of four or the only one there ever was — which is the
  // difference between a step nearly done and a step barely started. See stockFill, which draws
  // the two as one chip.
  parts: { id: string; side?: Side; n: number; of: number }[];
  // WHAT THIS GESTURE TOOK THAT WAS NOT THERE, by id. A gesture cannot consume a piece the
  // manual has not made, or has already built into something else — and with the inventory
  // running the length of the manual, nothing but a wrong answer can produce one.
  //
  // Reported rather than repaired. The two columns are one claim read twice: what a gesture
  // takes, and what is on the table for it to take. Where they disagree the app has no way to
  // know which is right, and quietly conjuring the missing piece into the stock would hide the
  // only evidence that the step was read wrongly.
  missing: string[];
  // AND WHAT IT HANDLED SEVERAL OF AT ONCE, by id. A gesture taking two side rails is a
  // gesture nobody performs: you position one rail, fasten it, and then start the second —
  // which is what a CYCLE is for, and the prompt now says so.
  //
  // The signature is exact and needs nothing but the row: a component part with a quantity
  // above one. Hardware is not counted — four screws going into one frame in one gesture is a
  // genuine handful and reads as one action.
  //
  // Reported like the one above, and for the same reason: the order of the gestures is the
  // model's answer, and nothing here can reorder it. The ids are handed out in that order and
  // referenced forward, so moving a row renumbers the pieces every later row names.
  atOnce: string[];
};

// A POOL LINE'S KEY: the part, and the hand where there is one. See Available.parts.
const POOL_GAP = '\u001f';
const poolKey = (id: string, side?: Side | null) => (side ? `${id}${POOL_GAP}${side}` : id);
const fromPoolKey = (key: string): { id: string; side?: Side } => {
  const [id, side] = key.split(POOL_GAP);
  return side ? { id, side: side as Side } : { id };
};
// Left, then right, and an unhanded line ahead of both — see the sort. A record and not a
// comparison written inline, so the order is stated once and read where it is used.
const HAND_ORDER: Record<Side | 'none', number> = { none: 0, left: 1, right: 2 };

export function availableAfter(
  rows: { key: string; label: string; step: unknown; sub: SubStep }[],
  items: PartItem[],
  pieces: Map<string, RowPieces>,
  // WHETHER A PART IS FURNITURE, for the several-at-once check. Which group a part falls in is
  // the ontology's answer and the ontology arrives through a hook, so it comes from the caller
  // — as the parts list itself does. A caller with no opinion passes none and the check simply
  // does not fire, which is the right behaviour before the categories have loaded.
  component: (p: PartItem | undefined) => boolean = () => false,
): Map<string, Available> {
  const out = new Map<string, Available>();
  let at: unknown;
  // KEYED BY PART AND HAND, not by part. A unit separator, because a part id is a number and a
  // hand is one of two words — nothing either of them can contain, so the two come apart again
  // by splitting rather than by parsing. A part with no hand keys under its bare id, which is
  // every part in most manuals and leaves the key exactly what it was.
  let left = new Map<string, number>();
  // What that step was handed, unspent — see the step boundary below.
  let supply = new Map<string, number>();
  // EVERY PIECE THE MANUAL HAS MADE AND NOT YET USED UP, carried the length of it. A piece
  // appears on the row that assembles it and stays until some gesture takes it into something
  // else, whether that gesture is two rows later or two steps later.
  //
  // IT USED TO EMPTY AT EVERY STEP BOUNDARY and be refilled with the pieces the new step
  // happens to NAME, looked up by where each was born. Two things followed. A piece nothing
  // ever picks up again — an assembly the manual simply leaves standing — vanished from the
  // column the moment its step ended, as though the step had eaten it. And a piece consumed by
  // the first gesture of a step was on the table for no row at all: step 3 finished the chair,
  // step 4 turned it over, and 303 was in step 4's parts column while step 4's stock said
  // there was no such thing.
  //
  // The refill machinery is what carried that: the map of where each piece was born, the
  // question "does this step name it", the per-step totals of pieces. A running inventory
  // needs none of them — a piece is on the table from the gesture that made it until the
  // gesture that spends it, which is what the column claims to be showing.
  //
  // A LIST, in the order the pieces were made.
  const made: (MadePiece & { n: number })[] = [];

  for (let i = 0; i < rows.length; i++) {
    const { key, step, sub } = rows[i];
    if (step !== at) {
      // A NEW STEP: total what its sub-steps take in, and start with that on the table.
      //
      // THE SUPPLIED PARTS ONLY, which is the half that really is per step. A rail left over
      // in step 2 is not stock for step 5 — the step's supply IS what its own sub-steps
      // consume, which is the rule the prompt states as CRITICAL — and a manual whose numbers
      // do not add up shows it as a part that runs out early. The pieces are the manual's, not
      // the step's, and are above.
      at = step;
      left = new Map();
      for (let j = i; j < rows.length && rows[j].step === step; j++) {
        for (const p of rows[j].sub.parts ?? []) {
          const part = items[Number(p.id)];
          if (!part || p.consumed === false || part.tool) continue;
          const k = poolKey(p.id, p.side);
          left.set(k, (left.get(k) ?? 0) + usedCount(p));
        }
      }
      // THE SAME TOTALS, KEPT WHOLE. `left` is spent down row by row and is the answer to "what
      // is on the table"; this is what the step started with, which is the other half of every
      // chip in that column and is gone from `left` by the second row.
      supply = new Map(left);
    }
    const missing: string[] = [];
    const atOnce = (sub.parts ?? [])
      .filter((p) => (p.qty ?? 1) > 1 && component(items[Number(p.id)]))
      .map((p) => p.id);
    for (const p of sub.parts ?? []) {
      const n = usedCount(p);
      // BY ITS ID, "101".
      //
      // A PIECE IS SPENT BY WHATEVER GESTURE NAMES IT, its kind included: every gesture leaves
      // a piece, and what it named is inside that piece rather than on the table.
      const piece = made.find((m) => m.id === p.id);
      if (piece) {
        // ALREADY USED UP is as wrong as never made, and reads worse: the id resolves, the
        // name is right, and the thing it names went into something else two gestures ago.
        if (piece.n < n) missing.push(p.id);
        // NEVER BELOW NOTHING. A row that over-consumes is one row's fault, and letting the
        // count go negative makes every row after it read as short of a piece that is
        // perfectly well there — one wrong answer marking the rest of the step.
        piece.n = Math.max(0, piece.n - n);
        continue;
      }
      // A SUPPLIED PART STILL GOES BY ITS OWN FLAG, and this is not the same question. That
      // flag is what the step's supply is summed from — the prompt makes the sum of the
      // consumed entries equal what the step was given — so spending a part a placing calls
      // unconsumed would take the pool below what anybody handed out.
      //
      // AND IT CANNOT BE MISSING. The pool is summed from the very entries that spend it, so
      // it lands on zero and never below; a part supplied in the wrong quantity is the
      // reconciliation's question and is answered against Phase 2's totals, which this walk
      // does not have.
      if (items[Number(p.id)]) {
        if (p.consumed === false) continue;
        // SPENT FROM ITS OWN HAND'S POOL. A gesture that takes the left rail must not draw the
        // right one down: they are two objects, and a step fitting both would otherwise empty
        // one line and leave the other full.
        const k = poolKey(p.id, p.side);
        if (left.has(k)) left.set(k, (left.get(k) ?? 0) - n);
        continue;
      }
      // Neither a piece on the table nor a part of this manual: an id from an answer written
      // against a numbering this document no longer has.
      missing.push(p.id);
    }
    // AND WHAT THIS ONE LEAVES, added after its own inputs are spent: a gesture does not hold
    // its output while it is making it, but the row's own column is read as "when this is
    // done", so it is there in this row's answer.
    const own = pieces.get(key)?.own;
    if (own) made.push({ ...own, n: 1 });
    // COPIED, NOT REFERENCED. A piece's count goes on changing as later gestures spend it, and
    // these objects are the ones being changed: stored by reference, every earlier row ended up
    // showing the FINAL count — a prepared screw made on row 1A read ×0 there because row 1E
    // eventually used the last of it.
    //
    // EVERY SUPPLIED PART, SPENT OR NOT. The step's stock is a fixed list and this column is
    // that list counting down, so a part drops to ×0 and stays on screen: "nothing left of it"
    // is an answer, and a chip vanishing between two rows makes the reader work out which one
    // went. A made piece is not stock — it comes into existence and is used up — so it appears
    // when it is made and goes when it is gone.
    out.set(key, {
      missing,
      atOnce,
      made: made.filter((m) => m.n > 0).map((m) => ({ ...m })),
      // IN ID ORDER, smallest first. The list is the same every row, so what a reader does
      // with it is compare one row against the one above — and a list ordered by first
      // appearance reshuffles nothing but reads as unordered, which makes them find each chip
      // again on every line. Numerically, since the ids are numbers and "10" sorts before "2"
      // as text.
      // BY ID, THEN BY HAND — left before right, and an un-sided entry before both. The two
      // hands of one part are read against each other, so they have to be adjacent and always
      // the same way round; sorting them by the word would put "droite" first in French and
      // "left" first in English, which is one list in two orders.
      parts: [...left.entries()]
        .map(([k, n]) => ({ ...fromPoolKey(k), n, of: supply.get(k) ?? n }))
        .sort((a, b) => Number(a.id) - Number(b.id) || HAND_ORDER[a.side ?? 'none']
          - HAND_ORDER[b.side ?? 'none']),
    });
  }
  return out;
}

// A LINE PER KIND, in the parts list's own order: the furniture, then what fastens it, then
// what you need on the table to do it (PART_GROUPS). A gesture's chips ran as one wrapped
// paragraph, which reads as one list of six things — and it is not: attaching a rail to a
// frame with two screws is two pieces of furniture, two fasteners and a key, and which of
// the three a chip belongs to is the first thing anybody wants from it. The wrap decided the
// lines before, so a screw and a frame shared a line whenever the widths happened to suit.
//
// A KIND WITH NOTHING IN IT DRAWS NO LINE. Three fixed lines would put two blank rows under
// the single chip of "turn the assembly over" and make its cell three times its content —
// which also undoes the vertical centring the cell is drawn with. The order is what makes a
// line readable without a heading, and the order holds whether or not all three are there.
//
// IN THE TABLE THE ORDER IS NOT WHAT MAKES IT READABLE — the heading over the column is, and
// the group can then be empty without anything being ambiguous about which one it was. That
// is the whole gain of `only`: the same three groups, in the same order, but named, and
// aligned down every gesture in the manual rather than only within one cell.
//
// WITHIN A LINE, WHAT IS ALREADY THERE COMES FIRST. A sub-step lists the piece it works ON
// as well as the pieces it consumes, and the two read as a sentence in that order: this rail
// and that frame, joined by these two screws. Stable otherwise, so the model's own order
// survives inside each half.
const held = (h: Held) => (h.part?.consumed === false ? 0 : 1);

// THE MARK FOR A PIECE THIS GESTURE USES UP, in one place because three kinds of chip ask for
// it and a chip that dashed without fading would read as a fourth kind. The availability
// column borrows the BORDER of it and not the fade — see stockFill, where the fading is the
// measure rather than the mark, and where a second fade over it would mean nothing twice. So
// the edge is named on its own and the mark is built from it, rather than the two spelling
// `border-dashed` apart from each other.
export const SPENT_EDGE = 'border-dashed';
export const consumedMark = (yes: boolean) => (yes ? `${SPENT_EDGE} opacity-70` : '');

// HOW MUCH OF THE STEP'S STOCK OF A PART IS STILL ON THE TABLE, drawn as the chip's own fill:
// full colour for the share that is left, the faded one for the share that is spent, with a
// hard edge between them.
//
// THE COUNT WAS ALREADY THERE and is not enough on its own. "×3" is a number a reader has to
// hold against another number they have to find — four of what was handed out — and the column
// exists to be read DOWN, a dozen chips at a time, which is exactly the reading a pair of
// numbers cannot be scanned in. A bar is read without being counted.
//
// AND IT SUBSUMES THE MARK IT REPLACES. A spent part faded the whole chip, text and all, which
// said one thing (there is none left) and cost the label's legibility to say it — the one
// column where the labels are longest. Empty is now simply nought per cent full: the same
// answer, at the same glance, with the words still black-on-amber white. The dashed border
// stays, because a chip at the bottom of a long column is read by its edge first.
//
// IN ALPHA, not in a lighter amber. What is behind the chip is white in the table and slate in
// the dark theme, and a solid blend can only be right against one of them; a transparent amber
// dims toward whichever is there, which is what the fade it replaces did.
const STOCK_FULL = '#d97706';
const STOCK_SPENT = 'rgba(217, 119, 6, 0.65)';
export const stockFill = (left: number, of: number): { background: string } | undefined => {
  // NOTHING TO SAY IS NOTHING DRAWN. A part with no supply behind it — a piece the manual made,
  // a record from before the totals were carried — keeps the flat chip it has always had
  // rather than a gauge reading zero, which would claim the one thing nobody asked.
  if (!(of > 0)) return undefined;
  const share = Math.max(0, Math.min(1, left / of));
  return {
    background: `linear-gradient(to right, ${STOCK_FULL} 0 ${share * 100}%, `
      + `${STOCK_SPENT} ${share * 100}% 100%)`,
  };
};

// WHAT A MADE PIECE IS MADE OF, opened from the chip that names it.
//
// A violet chip is the one thing in these tables that names something the manual never
// supplied: "102 – Side rail with barrel nut" is a name somebody has to trace back through
// the rows to understand, and the rows are elsewhere by the time they are reading it. The
// panel says it outright — the gesture that left it, and the pieces it took in.
//
// ONE LEVEL, deliberately. A piece is often made of another piece, and expanding the whole
// tree would print the manual's entire history under a chip. What it draws instead is the
// next level's chips, each of them openable in turn: the reader walks down as far as they
// care to, one press at a time.
// WHICH CHIP IS OPEN: a piece the manual never supplied, or a part it did. Both open the
// same window, because a reader pressing a chip is asking one question — what is this made
// of — and "nothing, it came in the box" is an answer to it rather than a reason to have no
// window.
export type OpenPiece =
  | { kind: 'made'; piece: MadePiece }
  | { kind: 'base'; id: string };

export function MadePieceCard({ open, items, pieces, onClose }: {
  open: OpenPiece;
  items: PartItem[];
  // Every piece known at that point in the sequence, so a component that is itself a piece
  // can be resolved — and its own components after it, all the way down.
  pieces: Map<string, MadePiece>;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const label = usePartLabel(items);
  if (open.kind === 'base') {
    return (
      // NOTHING TO COPY. One sentence, read where it stands — see CallModal, where the case
      // against offering the clipboard for it is written.
      <CallModal title={label(open.id)} copy={false} onClose={onClose}>
        <p className="text-center text-sm text-slate-700">{t('notice.steps.madeOfBase')}</p>
      </CallModal>
    );
  }
  const piece = open.piece;
  return (
    // AND NOTHING TO COPY HERE EITHER. What this window holds is a tree, drawn as chips and
    // the lines between them; read off the DOM it is a column of names with the shape — which
    // is the whole answer — gone.
    <CallModal title={`${piece.id} - ${piece.name}`} copy={false} onClose={onClose}>
      <p className="mb-4 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {t('notice.steps.madeOfIn').replace('{n}', piece.madeBy)}
      </p>
      {/* SCROLLS SIDEWAYS, because a tree is as wide as its widest level and the window is
          not. Centred while it fits, which is every ordinary case. */}
      <div className="overflow-x-auto">
        <div className="flex min-w-max justify-center px-2 pb-2">
          <PieceNode
            node={{ id: piece.id, qty: 1, piece }}
            items={items}
            pieces={pieces}
            seen={new Set([piece.id])}
          />
        </div>
      </div>
    </CallModal>
  );
}

// ONE NODE OF THE TREE: the chip, and — where it is a piece — the pieces it was made of,
// under it, joined by lines.
//
// RECURSIVE, ALL THE WAY TO THE BOTTOM. A piece is usually made of another piece, and the
// question a reader has when they press "104 – Frame with side rail" is not answered one
// level down: what they want is the pile of supplied parts at the end of it. So the whole
// thing is drawn at once and read as a shape, rather than being walked a press at a time.
//
// `seen` is the path back to the root, and it is not defensive programming: the piece map is
// keyed under legacy aliases as well as under real ids, and a wrong resolution that pointed a
// piece at itself would otherwise recur until the stack ran out. A cycle stops as a leaf.
//
// AND `seen` IS THE WHOLE BOUND. There was a depth cap of 8 beside it — a second belt, from
// when a step was four or five gestures and eight levels was more tree than anything could
// use. Written one unit at a time a step is thirty gestures, each leaving a piece made of the
// one before, so the chain is thirty deep and the drawing stopped a quarter of the way down:
// most of the manual missing, with nothing to say it had been cut.
//
// Nothing is needed in its place. A child already on the path is dropped, so a path is a
// strictly growing set of distinct pieces and cannot be longer than the manual has pieces.

export function PieceNode({ node, items, pieces, seen }: {
  node: { id: string; qty: number; piece?: MadePiece };
  items: PartItem[];
  pieces: Map<string, MadePiece>;
  seen: Set<string>;
}) {
  const label = usePartLabel(items);
  const kids = node.piece
    ? node.piece.from.map((p) => ({
      id: p.id,
      qty: p.qty,
      // ONLY A REAL PIECE ID RESOLVES. The map is also keyed under the maker's letter and —
      // in the chip window — under every supplied part that went INTO a piece; those are
      // aliases for reading answers written in older vocabularies, and looking a component up
      // through them made a rail and a nut both resolve to the piece they became, so 102 was
      // drawn as being made of two copies of itself. A piece's number is 101 and up; a
      // supplied part's is an index. Anything that is not a number at all is a maker's letter
      // and is looked up as given.
      piece: !Number.isFinite(Number(p.id)) || Number(p.id) >= MADE_ID_BASE
        ? pieces.get(p.id)
        : undefined,
    })).filter((p) => !p.piece || !seen.has(p.piece.id))
    : [];
  // OVER SEVERAL LINES, like every chip (see `nowrap`): a tree's width is the sum of the widest
  // row's chips, and "110 – Assembled chair frame with wood buttons" on one line makes a
  // drawing four times as wide as it is tall, which is the one shape a tree must not be.
  // A PIECE IS VIOLET, a supplied part amber. There was a third case — a part a PLACING left,
  // violet or amber depending on what was in it — and it is gone with the invented pieces that
  // needed it: a placing leaves a piece the model named, like every other gesture.
  //
  // FILLED IN THAT COLOUR HERE, where the chip lists elsewhere keep the outline. A tree is a
  // diagram — the eye is following shapes and lines, not reading a list of facts — and a
  // filled box is what makes a node an object at a glance. Its own class rather than a change
  // to `chip` / `outputChip`: those two are used wherever a part is named (the sub-step
  // lists, the available-parts column, Phase 5's ticked chips) and re-skinning this one
  // drawing must not re-skin all of those.
  //
  // FLAT, and at the tones the outline already used (violet-600 / amber-600, a step off the
  // borders' own 500s so white text has something to sit on). No gradient: a shaded box reads
  // as a button to press, and these are labels on a diagram.
  const box = `${node.piece
    ? 'border border-violet-700 bg-violet-600'
    : 'border border-amber-700 bg-amber-600'} rounded-xl px-3 py-1 text-xs font-semibold text-white`;
  const body = (
    <div className="flex flex-col items-center">
      <span className={`${box} max-w-[9rem] text-center leading-snug`}>
        {node.qty > 1
          ? <CountedLabel label={node.piece ? `${node.piece.id} - ${node.piece.name}` : label(node.id)} n={node.qty} />
          : node.piece ? `${node.piece.id} - ${node.piece.name}` : label(node.id)}
      </span>
      {!!kids.length && (
        // WHAT THIS PIECE WAS BUILT FROM. It was a measured box for a while: a cycle's dashed
        // ring had to stop above the history of a piece the cycle merely took in, and a band
        // through a tree is not a subtree, so the border was laid over the top and its bottom
        // read off this element. There are no cycles and no ring.
        <div className="flex flex-col items-center">
          {/* The stem out of this chip, then the row of children — each with its own stem up
              to the bar, and half a bar toward each sibling it has. Four straight lines make
              the fork; nothing is drawn diagonally, and nothing is an image.

              AND THE GESTURE ON THE STEM. A fork in this tree is not a fact about the shapes
              either side of it: it is a sub-step, the one that took those pieces and left
              this one, and its letter is what a reader needs to go and look at it. Drawn in
              the break between two segments of the stem rather than over it — a badge with a
              background of its own would have to know what colour it is standing on, and
              this tree is read on a page and inside a dark window both. */}
          <span className="h-3 w-px bg-slate-600" />
          {/* The letter at full strength, not dimmed: it is the one thing in this drawing a
              reader takes away with them — where to go and look at the gesture — and a grey
              badge on a grey stem read as part of the plumbing. */}
          <span className="rounded-full border border-slate-400 px-1.5 py-px text-[10px] font-semibold leading-tight text-slate-700">
            {node.piece!.madeBy}
          </span>
          <span className="h-3 w-px shrink-0 bg-slate-600" />
          <div className="flex items-start">
            {kids.map((kid, i) => (
              <div key={`${kid.id}-${i}`} className="relative flex flex-col items-center px-2">
                {/* The bar toward each sibling, at the top of this column: half its width to
                    the left, half to the right, so consecutive children draw one continuous
                    line and the outer ends stop at the first and last child. */}
                {i > 0 && (
                  <span className="absolute left-0 top-0 h-px w-1/2 bg-slate-600" />
                )}
                {i < kids.length - 1 && (
                  <span className="absolute right-0 top-0 h-px w-1/2 bg-slate-600" />
                )}
                {/* THE STEM IN THE FLOW, centred by the same flexbox that centres the chip
                    under it — as the parent's own stems are. It was absolute at `left-1/2`,
                    which is not the same place: a one-pixel box CENTRED in a column sits at
                    (width − 1) / 2, and one whose left edge is put on the 50% mark sits half a
                    pixel to the right of that. Two verticals that should be one line, half a
                    pixel apart, is the kink — it lands on whichever side of a device pixel the
                    rounding falls. */}
                <span className="h-4 w-px shrink-0 bg-slate-600" />
                <PieceNode
                  node={kid}
                  items={items}
                  pieces={pieces}
                  seen={kid.piece ? new Set([...seen, kid.piece.id]) : seen}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  return body;
}


export function SubStepChips({
  sub, items, only, empty = null, outputs, missing, atOnce, perPass,
}: {
  sub: SubStep;
  items: PartItem[];
  // WHAT TO DRAW WHERE THERE IS NOTHING, which only a caller with a grid to fill needs: the
  // table's dash. Nothing by default — under a sentence, an absent group is simply a group
  // that is not mentioned, and a dash there would answer a question nobody put.
  //
  // Given here rather than decided by the caller because deciding it means knowing whether
  // this gesture has anything in this group, and that is what this component is for. A cell
  // testing `sub.parts.length` itself would be a second opinion on the ontology's answer.
  empty?: ReactNode;
  // ONE OF THE THREE GROUPS: each is a column of its own in the table, drawn cell by cell.
  //
  // It replaces `divided`, which drew a rule between the groups inside ONE cell — the table's
  // grid faked in the only place the grid could not reach. Real columns do that properly: the
  // groups now line up down the whole table instead of only within a row, which is what the
  // rules were there to suggest and could not deliver.
  only: PartGroup;
  // WHAT AN EARLIER SUB-STEP ALREADY MADE OF THESE PARTS: part id -> that sub-step's output
  // name. Built by whoever holds the whole ordered sequence (see AllSubSteps), because a
  // gesture cannot know what the gestures before it left.
  //
  // A part listed "consumed": false whose id appears here is not the loose part any more —
  // the prompt's own rule is that a piece an earlier sub-step assembled is referenced by the
  // ids it was made FROM — so the two screws and the two washers of "drive the prepared
  // screw home" collapse to ONE violet chip saying what it is. Without this map they draw as
  // four ghosts of parts that were used up two rows ago.
  outputs?: Map<string, MadePiece>;
  // THE IDS THIS GESTURE TOOK THAT WERE NOT ON THE TABLE, from the walk that knows — see
  // availableAfter. Marked here rather than only counted there, because this is the column the
  // claim is made in: the reader is looking at "consumes 303" and the answer to "was there a
  // 303 to consume" belongs on that chip and not two columns away.
  missing?: string[];
  // AND THE IDS IT HANDLED SEVERAL OF AT ONCE. A different complaint from `missing` and drawn
  // differently: nothing here is wrong about the manual, the gesture is simply bigger than a
  // gesture — two side rails positioned in one motion, where a person does one and then the
  // other. So it is a note on the chip and not a strike through it.
  atOnce?: string[];
  // AND THE IDS COUNTED FOR THE RUN RATHER THAN THE PASS. Drawn like `atOnce`, which is the
  // same kind of complaint — the parts are right and a number is not — and worded to say which
  // number.
  perPass?: string[];
}) {
  const { t } = useLanguage();
  const label = usePartLabel(items);
  // Which chip's composition is being read, or none. Per cell rather than per page: the
  // window is about the chip that was pressed, and the cell is what holds it.
  const [open, setOpen] = useState<OpenPiece | null>(null);
  const whole = (p: UsedPart) => p.qty ?? 1;
  const categories = useCategories();
  const parts = sub.parts ?? [];
  // A tool the sub-step's parts already name is not listed twice: the parts list is what the
  // gesture handles, and a thing handled once is one chip.
  const tools = (sub.tools ?? []).filter((id) => !parts.some((p) => p.id === id));
  if (!parts.length && !tools.length) return empty;

  // Which line a chip belongs on: the ontology's answer for the part, except that a part
  // flagged as a tool IS a tool whatever folder it sits in — that flag is what the parts
  // phase decides an allen key by, and it is the same question this line is asking.
  //
  // ASKED OF THE CHIP AND NOT OF ITS ID, which is the whole of a bug worth writing down. This
  // looked the id up in `outputs` itself, and that map is keyed under the maker's letter AND
  // under every part id that went into the piece — the old vocabulary, see madePieces. So the
  // second gesture of a cycle put its own loose barrel nut in the components column: 1B had
  // consumed part 7, so '7' aliased 1B's assembled component, and the alias answered for a part
  // 1C picks up fresh. `madeAlready` already decides that question — it consults the flag for
  // the numeric spellings and not for the piece's own — and `output` on the chip IS its answer.
  // Two readings of one map were two opinions about what a chip is: drawn amber as a loose
  // part, filed as the piece two rows up.
  const lineOf = (h: Held) => {
    // A piece an earlier sub-step made is sorted by what the KIND says it is, since it has no
    // catalogue row to ask — see MadePiece.
    if (h.output) return h.output.group;
    const p = items[Number(h.id)];
    if (!p) return PART_GROUPS[0];
    return p.tool ? 'Tools' : groupOfCategory(p.category, categories);
  };

  // A part merely handled that an earlier sub-step already used up IS that sub-step's output,
  // by the rule the analysis was given. Several of them can be the same piece — a screw and
  // its washer are one prepared screw — so they are grouped by that name and drawn once, at
  // the position of the first of them, which keeps the model's own order.
  // TWO WAYS IN, because two vocabularies are in the library. A sub-step analysed today names
  // the piece by its maker's letter — one entry, one chip, and it may perfectly well be
  // consumed. One analysed before that names the ids it was made FROM with "consumed": false,
  // and those collapse into the same chip. The letter cannot be mistaken for a part id (parts
  // are numbers), so one map holds both keys, and the "consumed": false test is kept for the
  // numeric ones alone: without it a loose screw picked up after prepared screws were made
  // would be drawn as a prepared screw.
  const madeAlready = (p: UsedPart) => {
    const piece = outputs?.get(p.id);
    if (!piece) return undefined;
    // WHICH SPELLING WAS USED, and it decides whether the flag is consulted. An entry naming
    // the piece ITSELF — by its id, 101 up, or by its maker's letter — is that piece however
    // it is flagged, and a combining that takes it in consumes it. An entry naming a part the
    // piece was made FROM is the oldest spelling, from before pieces had ids, and only means
    // the piece where the part is NOT consumed here: without that test a loose screw picked
    // up after prepared screws were made would be drawn as a prepared screw.
    //
    // The test is whether the id resolves to a supplied part, because that is the difference:
    // 101 is past the end of the list, and "b" is not a number. It used to test for a number,
    // which was true of the letters and became false the day the pieces were numbered too —
    // and every made piece went back to being drawn as "#101" in a part's amber.
    return items[Number(p.id)] ? (p.consumed === false ? piece : undefined) : piece;
  };
  const seenOutput = new Set<string>();
  const all: Held[] = [
    ...parts.flatMap((p, i) => {
      const made = madeAlready(p);
      if (!made) return [{ key: `p-${p.id}-${i}`, id: p.id, part: p }];
      if (seenOutput.has(made.id)) return [];
      seenOutput.add(made.id);
      return [{ key: `o-${made.id}`, id: p.id, part: p, output: made }];
    }),
    ...tools.map((id) => ({ key: `t-${id}`, id, part: null })),
  ];
  const lines = PART_GROUPS
    .filter((group) => group === only)
    .map((group) => ({
      group,
      members: all.filter((h) => lineOf(h) === group).sort((a, b) => held(a) - held(b)),
    }))
    .filter((line) => line.members.length);
  // NOTHING FOR THIS GROUP IS NOTHING AT ALL, and the caller decides what to draw in its
  // place — a dash, in the table, which is the mark it uses for every question a gesture
  // answers with none.
  if (!lines.length) return empty;

  return (
    <div className="flex flex-col items-center gap-1.5">
      {/* WHAT IS THIS MADE OF — one press, on any chip. A violet chip names something the
          manual never supplied, and tracing it back meant finding the row that left it; an
          amber one names something it did, and the answer there is "nothing, it came in the
          box", which is worth being told rather than being left to infer from a chip that
          does not respond. */}
      {open && (
        <MadePieceCard
          open={open}
          items={items}
          pieces={outputs ?? new Map()}
          onClose={() => setOpen(null)}
        />
      )}
      {lines.map(({ group, members }) => (
        <div key={group} className="flex flex-wrap justify-center gap-2">
          {members.map(({ key, id, part, output }) => (
            <button
              key={key}
              type="button"
              // THE ONE PRESSABLE THING IN A ROW OF THE OVERVIEW, and nothing above it is
              // listening: the row it sits in was a control once — pressing it walked the
              // navigator to that sub-step — and the press had to be stopped here so that
              // opening the window did not also navigate away from the table it opened over.
              // The row is not a control now (see AllSubSteps), so there is nothing to stop.
              onClick={() => setOpen(output ? { kind: 'made', piece: output } : { kind: 'base', id })}
              title={
                missing?.includes(id) ? t('notice.steps.subMissing')
                  : perPass?.includes(id) ? t('notice.steps.subPerPass')
                    : atOnce?.includes(id) ? t('notice.steps.subAtOnce')
                      : output ? t('notice.steps.subFromEarlier')
                        : part?.consumed === false ? t('notice.steps.subHandled') : undefined
              }
              // DASHED AND FADED IS CONSUMED: this gesture uses the part up, and the broken
              // border is the piece stopping being a piece. The two marks travel together —
              // one fact, said twice, for the reader who catches weight before shape — and
              // they marked the opposite until now, which put the mark on the majority of the
              // chips and the plain border on the thing the sub-step is actually about.
              //
              // A TOOL IS NEITHER, which is why this reads off `part` first: a tool has no
              // entry in the parts list at all, so "is it consumed" has no answer rather than
              // the answer false — and a screwdriver drawn dashed would say the one thing
              // about a tool that is never true. It takes the wrench and a plain border.
              //
              // AND A PIECE AN EARLIER SUB-STEP MADE IS ALWAYS CONSUMED, whatever its entry
              // says and whatever kind of gesture this is. Its entries say "consumed": false
              // because their units were counted where they were assembled — that flag is
              // about the ids, not about this gesture — so reading it here would draw every
              // made piece as merely held.
              //
              // It read off the FAMILY for a while, on the argument that a combining leaves
              // one piece where there were two while a placing merely moves what is already
              // there. That stopped being true when a placing started leaving a piece of its
              // own: "position the assembled chair (302) onto its legs" leaves 401, and 302 is
              // inside it. Every gesture now leaves something its components are part of, so a
              // piece a gesture names is a piece that gesture uses up.
              className={`${output ? outputChip : chip} ${
                output ? consumedMark(true)
                  : part ? consumedMark(part.consumed !== false)
                    : 'inline-flex items-center gap-1.5'} ${
                // A CHIP FOR SOMETHING THAT IS NOT THERE. Struck through and in the app's
                // warning colour, over whatever the chip already was: this is not a fourth
                // kind of chip, it is any chip with something wrong about it, and it has to
                // stay legible as the piece it names — which is the thing the reader is being
                // asked to doubt.
                missing?.includes(id) ? '!border-rose-500 !text-rose-500 line-through'
                  // Amber-lit rather than struck out: the parts are there and something about
                  // the WRITING of the row is wrong — where it was cut, or which number was
                  // put in — and a reader has to go on reading the chip as the part it names.
                  : atOnce?.includes(id) || perPass?.includes(id)
                    ? '!border-amber-500 !border-2' : ''
              } cursor-pointer hover:opacity-80`}
            >
              {!part && <Wrench className="h-3 w-3 shrink-0 opacity-70" />}
              {part
                ? <CountedLabel label={output ? `${output.id} - ${output.name}` : label(id, part?.side)} n={whole(part)} />
                : output ? `${output.id} - ${output.name}` : label(id)}
              {/* WHICH END OF THE FITTING THIS PART IS — "(add.)" for the one being brought
                  and put onto something, "(rec.)" for the one receiving it. Phase 4's answer
                  (see PartRole), printed where the part is, because that is the only place it
                  means anything: a row of chips is what a reader checks the sentence against,
                  and "which of these moves" is the first thing the sentence claims.

                  AFTER THE COUNT, and in the chip's own ink. It sat between the name and the
                  count, dimmed, which put a faded aside in the middle of the one string a
                  reader scans — the id, the name, how many — and broke it in two. Last and at
                  full strength it is a word at the end of a phrase rather than an interruption
                  inside one.

                  NOT ON A TOOL and not on a made piece drawn as itself: a tool is neither end
                  of the fitting, and an output chip in the AVAILABLE column is a piece sitting
                  on the table rather than a part in a gesture. Both are `part`-less or
                  role-less here, so the test is simply whether there is a role to print. */}
              {part?.role && (
                <span className="ml-1">{t(`notice.steps.role.${part.role}`)}</span>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
